import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import { PreKSettings as KSettings, DiagnosticAssessmentResult } from '../../types/kAssessmentTypes';
import { useKTTS, K_QUESTION_SCREEN_AUDIO } from '../../hooks/useKAudio';
import { useAuth } from '../../context/AuthContext';
import { progressQueue } from '../../services/ProgressQueueService';
import { recordPageTransition, completePreKAssessment, getTrackFacts } from '../../config/api';
import { FactStatus } from '../../types/progress';
import { TRACK_RANGES } from '../../types/constants';
import { NUMERAL_FONT_STYLE } from '../../styles/design-system/typography';
import WarmupTransitionScreen from './WarmupTransitionScreen';
import { AssessmentHeader } from '../PreKAssessment/shared';
import ListenButton from '../common/ListenButton';

// TTS frequency config (same as Subitizing)
const TTS_CONFIG = {
  // Play audio every N questions during test phase
  TEST_TTS_INTERVAL: 3,
};

interface ComparisonQuestionScreenProps {
  settings: KSettings;
  onComplete: (results: DiagnosticAssessmentResult) => void;
}

interface ComparisonFact {
  factId: string;
  operand1: number;
  operand2: number;
  result: number;
}

const ComparisonQuestionScreen: React.FC<ComparisonQuestionScreenProps> = ({ settings, onComplete }) => {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [wallClockTimeRemaining, setWallClockTimeRemaining] = useState(60000); // 60 seconds wall clock limit
  const [wallClockStarted, setWallClockStarted] = useState(false); // Flag to track when wall clock starts
  const [activeTimeElapsed, setActiveTimeElapsed] = useState(0); // Track actual active thinking time
  const [questionTimeRemaining, setQuestionTimeRemaining] = useState(8600); // 8.6 seconds per question
  const [questionTimerActive, setQuestionTimerActive] = useState(false); // Per-question timer
  const [isWarmup, setIsWarmup] = useState(true);
  const [warmupCount, setWarmupCount] = useState(0);
  const [showTransitionScreen, setShowTransitionScreen] = useState(false);
  const [comparisonFacts, setComparisonFacts] = useState<ComparisonFact[]>([]);
  const [currentFact, setCurrentFact] = useState<ComparisonFact | null>(null);
  const [waitingForCorrectAnswer, setWaitingForCorrectAnswer] = useState(false);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [correctAnswers, setCorrectAnswers] = useState(0);
  const [timerActive, setTimerActive] = useState(false);
  const [ttsHasPlayed, setTtsHasPlayed] = useState(false);
  const [wrongAnswer, setWrongAnswer] = useState<number | null>(null);
  const [hasAnswered, setHasAnswered] = useState(false);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);

  // Comparison-specific states
  const [questionPhase, setQuestionPhase] = useState<'ready' | 'question'>('ready');

  // Auth context for user ID
  const { user, updateUserField } = useAuth();

  // Timing references
  const testStartTime = useRef<number>(Date.now());
  const questionStartTime = useRef<number>(Date.now());
  const timerActiveStartTime = useRef<number>(0);
  const accumulatedTimerTime = useRef<number>(0);
  const questionActiveStartRef = useRef<number | null>(null);
  const questionAccumulatedTimeRef = useRef<number>(0);
  const hasCompletedRef = useRef<boolean>(false);
  const isFirstQuestionRef = useRef(true);

  // TTS integration
  const { playTTSWithCallback, isTTSPlaying, playChime } = useKTTS();


  // Determine fact status based on correctness and response time (targeting 4s fluency for comparison)
  const determineFactStatus = useCallback((isCorrect: boolean, timeSpentMs: number): FactStatus => {
    if (!isCorrect) {
      return 'learning';
    }

    const timeSpentSeconds = timeSpentMs / 1000;

    if (timeSpentSeconds > 4) {
      return 'accuracyPractice';  // Need to work on speed
    } else {
      return 'mastered';  // Already at target 4s fluency
    }
  }, []);

  // Load comparison facts from backend using batch API
  useEffect(() => {
    const loadComparisonFacts = async () => {
      try {
        const facts = await getTrackFacts('TRACK_K_COMPARING');
        // Convert track facts to comparison facts format and shuffle
        const comparisonFacts = facts
          .map(fact => ({
            factId: fact.factId,
            operand1: fact.operand1,
            operand2: fact.operand2,
            result: fact.result
          }))
          .sort(() => Math.random() - 0.5); // Shuffle once

        setComparisonFacts(comparisonFacts);
        if (comparisonFacts.length > 0) {
          setCurrentFact(comparisonFacts[0]);
        }
      } catch (error) {
        console.error('Error loading comparison facts:', error);
      }
    };

    loadComparisonFacts();
  }, []);

  // Set track ID and report page transition when component mounts
  useEffect(() => {
    sessionStorage.setItem('activeTrackId', 'TRACK_K_COMPARING');

    if (user?.userId) {
      recordPageTransition({
        userId: user.userId,
        trackId: 'TRACK_K_COMPARING',
        page: 'assessment'
      }).catch((error: any) => {
        console.error('Error recording page transition:', error);
      });
    }
  }, [user?.userId]);

  // Update question start time when moving to test phase
  useEffect(() => {
    if (!isWarmup) {
      questionStartTime.current = Date.now();
      accumulatedTimerTime.current = 0;
      timerActiveStartTime.current = 0;
      // Reset per-question timing
      questionAccumulatedTimeRef.current = 0;
    }
  }, [isWarmup, currentFact]);

  // Track timer active time
  useEffect(() => {
    if (isWarmup) return;

    if (timerActive) {
      timerActiveStartTime.current = Date.now();
      questionActiveStartRef.current = Date.now();
    } else {
      if (timerActiveStartTime.current > 0) {
        const activeTime = Date.now() - timerActiveStartTime.current;
        accumulatedTimerTime.current += activeTime;
        timerActiveStartTime.current = 0;
      }
      if (questionActiveStartRef.current) {
        const questionActiveTime = Date.now() - questionActiveStartRef.current;
        questionAccumulatedTimeRef.current += questionActiveTime;
        questionActiveStartRef.current = null;
      }
    }
  }, [timerActive, isWarmup]);

  // Wall clock timer countdown (strict 60s limit including VOs and transitions)
  useEffect(() => {
    // Don't run during warmup or before wall clock starts
    if (isWarmup || !wallClockStarted || wallClockTimeRemaining <= 0) {
      if (wallClockTimeRemaining <= 0 && wallClockStarted && user?.userId && !hasCompletedRef.current) {
        // Wall clock expired - end test immediately
        hasCompletedRef.current = true;
        handleTestComplete({
          userId: user.userId,
          testResults: [],
          recommendedStartSkill: 1,
          placementReason: 'Time limit reached (60 seconds)',
          completedAt: new Date(),
          totalQuestions,
          correctAnswers,
          activeTimeElapsed
        });
      }
      return;
    }

    const timer = setTimeout(() => {
      setWallClockTimeRemaining(prev => Math.max(0, prev - 100));
    }, 100);

    return () => clearTimeout(timer);
  }, [wallClockTimeRemaining, wallClockStarted, isWarmup, user?.userId, totalQuestions, correctAnswers, activeTimeElapsed]);

  // Active time tracking (for analytics - no limit)
  useEffect(() => {
    // Only track active time during test phase when timer is active
    if (isWarmup || !timerActive) return;

    const timer = setTimeout(() => {
      setActiveTimeElapsed(prev => prev + 100);
    }, 100);

    return () => clearTimeout(timer);
  }, [timerActive, isWarmup, activeTimeElapsed]);

  // Wrapper to handle page transition reporting before completing
  const handleTestComplete = useCallback(async (results: DiagnosticAssessmentResult) => {
    if (user?.userId) {
      try {
        await recordPageTransition({
          userId: user.userId,
          trackId: 'TRACK_K_COMPARING',
          page: 'other'
        });
      } catch (error) {
        console.error('Error recording final page transition:', error);
      }

      // Save K assessment data to database and sync focusTrack
      try {
        const result = await completePreKAssessment(user.userId, 'TRACK_K_COMPARING', {
          totalQuestions: results.totalQuestions || 0,
          correctAnswers: results.correctAnswers || 0,
          activeTimeElapsed: results.activeTimeElapsed || 0
        });
        if (result.focusTrack) {
          updateUserField({ focusTrack: result.focusTrack });
        }
      } catch (error) {
        console.error('Failed to save K assessment data:', error);
      }
    }
    onComplete(results);
  }, [user?.userId, onComplete, updateUserField]);

  // Start the comparison sequence: ready → show numbers and question
  const startComparisonSequence = useCallback(() => {
    setTtsHasPlayed(false);
    setTimerActive(false);

    if (isFirstQuestionRef.current) {
      // First question: show "Get ready..." phase with delay
      setQuestionPhase('ready');
      setTimeout(() => {
        setQuestionPhase('question');
      }, 500);
    } else {
      // Subsequent questions: skip ready phase, show question immediately
      setQuestionPhase('question');
    }
  }, []);

  // Play TTS when entering question phase (not on every question)
  useEffect(() => {
    if (questionPhase === 'question' && !ttsHasPlayed) {
      // Only play audio on first warmup question, or first test question
      const shouldPlayAudio = isWarmup
        ? warmupCount === 0
        : currentQuestionIndex === 0;

      if (settings.speechEnabled && shouldPlayAudio) {
        playTTSWithCallback(K_QUESTION_SCREEN_AUDIO.comparing.instruction, () => {
          if (!isWarmup) {
            setTimerActive(true); // Start timer only after TTS completes (test phase only)
            setQuestionTimerActive(true); // Start question timer
          }
          setTtsHasPlayed(true);
        }).catch(() => {
          if (!isWarmup) {
            setTimerActive(true);
            setQuestionTimerActive(true); // Start question timer
          }
          setTtsHasPlayed(true);
        });
      } else {
        // No audio to play - start timer immediately
        if (!isWarmup) {
          setTimerActive(true);
          setQuestionTimerActive(true);
        }
        setTtsHasPlayed(true);
      }
    }
  }, [questionPhase, ttsHasPlayed, settings.speechEnabled, playTTSWithCallback, isWarmup, warmupCount, currentQuestionIndex]);

  // Start sequence when fact loads and phase is ready
  useEffect(() => {
    if (currentFact && questionPhase === 'ready') {
      startComparisonSequence();
    }
  }, [currentFact, questionPhase, startComparisonSequence]);

  // Reset state for new question
  const resetQuestionState = useCallback((callback?: () => void) => {
    isFirstQuestionRef.current = false;
    setTimerActive(false);
    setQuestionTimerActive(false); // Pause question timer during transitions
    setQuestionTimeRemaining(8600); // Reset question timer to 8.6 seconds
    setWrongAnswer(null);
    setTtsHasPlayed(false);
    setQuestionPhase('ready'); // Reset phase to ready for new sequence
    setHasAnswered(false);
    setSelectedAnswer(null);

    // Execute callback after state reset if provided
    if (callback) {
      setTimeout(callback, 50);
    }
  }, []);

  // Atomically advance to next fact: increment index and set fact based on new index
  const advanceToNextFact = useCallback((callback?: () => void) => {
    if (comparisonFacts.length === 0) return;

    setCurrentQuestionIndex(prevIndex => {
      const nextIndex = (prevIndex + 1) % comparisonFacts.length;
      const nextFact = comparisonFacts[nextIndex];

      // Update fact synchronously to maintain state consistency
      setCurrentFact(nextFact);
      if (callback) {
        callback();
      }

      return nextIndex;
    });
  }, [comparisonFacts]);

  // Handle warmup progression
  const advanceWarmupQuestion = useCallback(() => {
    if (waitingForCorrectAnswer) {
      setWaitingForCorrectAnswer(false);
    }

    const newWarmupCount = warmupCount + 1;

    // Check if warmup is complete (fixed 3 questions)
    if (newWarmupCount >= 3) {
      // Show transition screen instead of immediately starting test
      setShowTransitionScreen(true);
      return;
    }

    setWarmupCount(newWarmupCount);
    resetQuestionState(() => advanceToNextFact());
  }, [waitingForCorrectAnswer, warmupCount, resetQuestionState, advanceToNextFact]);

  // Handle transition screen completion - start the actual test
  const handleTransitionComplete = useCallback(() => {
    setShowTransitionScreen(false);
    setIsWarmup(false);
    setWarmupCount(0);
    setCurrentQuestionIndex(0);
    setWallClockStarted(true);
    resetQuestionState(() => advanceToNextFact());
  }, [resetQuestionState, advanceToNextFact]);

  // Handle test progression
  const advanceTestQuestion = useCallback(() => {
    if (waitingForCorrectAnswer) {
      setWaitingForCorrectAnswer(false);
      resetQuestionState(() => advanceToNextFact());
      return;
    }

    setCorrectAnswers(correctAnswers + 1);
    setTotalQuestions(totalQuestions + 1);
    resetQuestionState(() => advanceToNextFact());
  }, [waitingForCorrectAnswer, correctAnswers, totalQuestions, resetQuestionState, advanceToNextFact]);

  const handleAnswerClick = useCallback((selectedNumber: number) => {
    if (!currentFact) return;

    // If waiting for correct answer, only allow correct selection
    if (waitingForCorrectAnswer && selectedNumber !== currentFact.result) return;

    // If not waiting and already answered, don't allow more clicks
    if (!waitingForCorrectAnswer && hasAnswered) return;

    // Force timer to be active since user has responded
    if (!isWarmup) {
      setTimerActive(true);
      setQuestionTimerActive(true); // Ensure question timer is active
    }
    setTtsHasPlayed(true);
    setHasAnswered(true);
    setSelectedAnswer(selectedNumber);

    const isCorrect = selectedNumber === currentFact.result;

    // Calculate per-question active response time for test questions
    let timerActiveResponseTime = 0;
    if (!isWarmup) {
      timerActiveResponseTime = questionAccumulatedTimeRef.current + (questionActiveStartRef.current ? Date.now() - questionActiveStartRef.current : 0);
    }

    // Report progress data for test questions
    if (!isWarmup && user?.userId && !waitingForCorrectAnswer) {
      try {
        const factStatus = determineFactStatus(isCorrect, timerActiveResponseTime);

        progressQueue.enqueueProgressUpdate(
          user.userId,
          {
            facts: {
              [currentFact.factId]: {
                attempts: 1,
                correct: isCorrect ? 1 : 0,
                timeSpent: timerActiveResponseTime,
                status: factStatus
              }
            }
          }
        );
      } catch (error) {
        console.error('Error reporting K comparison progress:', error);
      }
    }

    // Handle answer logic with immediate audio feedback
    if (isWarmup) {
      if (isCorrect) {
        playChime().then(() => {
          setTimeout(() => {
            advanceWarmupQuestion();
          }, 300);
        });
      } else {
        setWrongAnswer(selectedNumber);
        setTimeout(() => setWrongAnswer(null), 1000);

        // Replay instruction after boop
        if (settings.speechEnabled) {
          setTimeout(() => {
            playTTSWithCallback(K_QUESTION_SCREEN_AUDIO.comparing.instruction).catch(() => {});
          }, 300);
        }

        setWaitingForCorrectAnswer(true);
        setHasAnswered(false); // Reset so user can try again
      }
    } else {
      // Quiz mode: neutral feedback with chime for all answers, same timing
      if (!waitingForCorrectAnswer && !isCorrect) {
        setTotalQuestions(totalQuestions + 1);
      }

      playChime().then(() => {
        setTimeout(() => {
          if (isCorrect) {
            advanceTestQuestion();
          } else {
            resetQuestionState(() => advanceToNextFact());
          }
        }, 300); // 300ms delay for all answers
      });
    }
  }, [
    currentFact, waitingForCorrectAnswer, hasAnswered, isWarmup, timerActive,
    user, isTTSPlaying, settings.speechEnabled, determineFactStatus,
    playChime, playTTSWithCallback, advanceWarmupQuestion, advanceTestQuestion,
    totalQuestions, advanceToNextFact, resetQuestionState
  ]);

  // 8.6-second per-question countdown timer
  useEffect(() => {
    // Don't run question timer during warmup, when not active, or when already answered
    if (isWarmup || !questionTimerActive || questionTimeRemaining <= 0 || hasAnswered) {
      if (questionTimeRemaining <= 0 && questionTimerActive && !isWarmup && !hasAnswered) {
        // Question time expired - auto advance without showing correct answer
        if (waitingForCorrectAnswer) {
          // If waiting for correct answer, just advance
          setWaitingForCorrectAnswer(false);
          resetQuestionState(() => advanceToNextFact());
        } else {
          // Count as an incorrect attempt and advance
          setTotalQuestions(totalQuestions + 1);

          // Report progress data for timeout (treat as incorrect answer)
          if (user?.userId && currentFact) {
            try {
              const factStatus = determineFactStatus(false, 8600); // Use full question time as timeout response time

              progressQueue.enqueueProgressUpdate(
                user.userId,
                {
                  facts: {
                    [currentFact.factId]: {
                      attempts: 1,
                      correct: 0, // Timeout is incorrect
                      timeSpent: questionAccumulatedTimeRef.current + (questionActiveStartRef.current ? Date.now() - questionActiveStartRef.current : 0), // Actual time spent on this question
                      status: factStatus
                    }
                  }
                }
              );
            } catch (error) {
              console.error('Error reporting K comparison timeout progress:', error);
            }
          }

          resetQuestionState(() => advanceToNextFact());
        }
      }
      return;
    }

    const timer = setTimeout(() => {
      setQuestionTimeRemaining(prev => prev - 100);
    }, 100);

    return () => clearTimeout(timer);
  }, [questionTimeRemaining, questionTimerActive, isWarmup, waitingForCorrectAnswer, totalQuestions, hasAnswered, resetQuestionState, advanceToNextFact, user?.userId, currentFact, determineFactStatus]);

  if (!currentFact || comparisonFacts.length === 0) {
    return <div className="min-h-screen bg-gradient-to-br from-purple-100 via-pink-50 to-blue-100 flex items-center justify-center">
      <div className="text-2xl">Loading...</div>
    </div>;
  }

  // Show transition screen after warmup completes
  if (showTransitionScreen) {
    return (
      <WarmupTransitionScreen
        onComplete={handleTransitionComplete}
        speechEnabled={settings.speechEnabled}
      />
    );
  }

  return (
    <div className="h-screen overflow-hidden bg-gradient-to-br from-purple-100 via-pink-50 to-blue-100 flex flex-col">
      {/* Header */}
      <AssessmentHeader
        isWarmup={isWarmup}
        showTimer={true}
        wallClockTimeRemaining={wallClockTimeRemaining}
        wallClockStarted={wallClockStarted}
        isTTSPlaying={isTTSPlaying}
        timerHideable={true}
      />

      {/* Listen Button - only during question phase */}
      {settings.speechEnabled && questionPhase === 'question' && (
        <div className="flex justify-center pt-4">
          <ListenButton
            onClick={() => playTTSWithCallback(K_QUESTION_SCREEN_AUDIO.comparing.instruction)}
            disabled={hasAnswered}
            isPlaying={isTTSPlaying}
          />
        </div>
      )}

      {/* Main Content Area */}
      <div className="flex-1 flex items-center justify-center px-6 py-4 pb-8">
        {/* Side-by-side layout container */}
        <div className="w-full max-w-6xl flex items-center justify-center gap-24">

          {/* Ready Phase: Waiting - only shown on first question */}
          {questionPhase === 'ready' && isFirstQuestionRef.current && (
            <div className="text-center">
              <div className="text-2xl lg:text-3xl text-gray-600 animate-pulse mb-2">Get ready...</div>
              <div className="text-lg text-gray-500">Which is greater?</div>
            </div>
          )}

          {/* Question Phase: Show answer choices only */}
          {questionPhase === 'question' && (
            <div className="w-full max-w-md lg:max-w-lg">
              <div className="flex flex-col gap-8">
                {[currentFact.operand1, currentFact.operand2].map((choice, index) => (
                  <motion.button
                    key={choice}
                    initial={isFirstQuestionRef.current ? { opacity: 0, y: 30, rotate: -5 } : { opacity: 0 }}
                    animate={isFirstQuestionRef.current ? { opacity: 1, y: 0, rotate: 0 } : { opacity: 1 }}
                    transition={isFirstQuestionRef.current ? { duration: 0.4, delay: index * 0.1 } : { duration: 0.15 }}
                    whileHover={{ scale: 1.08, rotate: 1 }}
                    whileTap={{ scale: 0.92 }}
                    onClick={() => handleAnswerClick(choice)}
                    disabled={hasAnswered}
                    className={`
                      h-32 lg:h-40 rounded-3xl font-bold text-5xl lg:text-6xl shadow-xl transition-all duration-300
                      ${!isWarmup && hasAnswered
                        ? selectedAnswer === choice
                          ? 'bg-blue-400 text-white border-4 border-blue-500 shadow-blue-300'
                          : 'bg-blue-200 text-blue-400 border-4 border-blue-300'
                        : wrongAnswer === choice
                        ? 'bg-red-500 text-white border-4 border-red-700 shadow-red-300 animate-pulse'
                        : hasAnswered && choice === currentFact.result
                        ? 'bg-green-500 text-white border-4 border-green-700 shadow-green-300'
                        : hasAnswered && choice !== currentFact.result
                        ? 'bg-gray-300 text-gray-500 border-4 border-gray-400'
                        : waitingForCorrectAnswer && choice === currentFact.result
                        ? 'bg-green-500 text-white border-4 border-green-700 shadow-green-300 animate-pulse'
                        : waitingForCorrectAnswer && choice !== currentFact.result
                        ? 'bg-gray-300 text-gray-500 border-4 border-gray-400 cursor-not-allowed'
                        : 'bg-gradient-to-br from-blue-400 to-blue-600 hover:from-blue-500 hover:to-blue-700 text-white border-4 border-blue-700'
                      }
                      ${hasAnswered && !waitingForCorrectAnswer ? 'cursor-not-allowed' : waitingForCorrectAnswer && choice !== currentFact.result ? 'cursor-not-allowed' : 'cursor-pointer hover:shadow-2xl transform-gpu'}
                    `}
                    style={NUMERAL_FONT_STYLE}
                  >
                    {choice}
                  </motion.button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ComparisonQuestionScreen;